import React, { useMemo, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import enGbLocale from "@fullcalendar/core/locales/en-gb";
import esLocale from "@fullcalendar/core/locales/es";
import { Box, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  agendaEventToFullCalendar,
  getAgendaEventColors,
  getAgendaStatusColors,
  parseManualEventId,
} from "./agendaUtils";

const FULLCALENDAR_LOCALES = {
  "pt-BR": ptBrLocale,
  "pt-br": ptBrLocale,
  pt: ptBrLocale,
  en: enGbLocale,
  es: esLocale,
};

function resolveFullCalendarLocale(language, allDayText, noEventsText) {
  const normalized = String(language || "pt-BR").trim();
  const base =
    FULLCALENDAR_LOCALES[normalized] ||
    FULLCALENDAR_LOCALES[normalized.toLowerCase()] ||
    FULLCALENDAR_LOCALES[normalized.split(/[-_]/)[0]] ||
    FULLCALENDAR_LOCALES["pt-BR"];
  return {
    ...base,
    allDayText,
    noEventsText,
  };
}

const AgendaCalendarCore = forwardRef(function AgendaCalendarCore(
  {
    events = [],
    initialView = "timeGridDay",
    height = "100%",
    headerToolbar,
    onDatesChange,
    onEventClick,
    onDateClick,
    onEventDrop,
    onEventResize,
    nowIndicator = true,
    slotMinTime = "06:00:00",
    slotMaxTime = "23:00:00",
    editableManualOnly = true,
    compact = false,
    viewKey,
  },
  ref
) {
  const { t, i18n } = useTranslation("teamAgenda");
  const theme = useTheme();
  const calendarRef = useRef(null);
  const colors = useMemo(() => getAgendaEventColors(theme), [theme]);
  const statusColors = useMemo(() => getAgendaStatusColors(theme), [theme]);
  const fcLocale = useMemo(
    () => resolveFullCalendarLocale(i18n.language, t("allDay"), t("noEvents")),
    [i18n.language, t]
  );

  useImperativeHandle(ref, () => ({
    getApi: () => calendarRef.current?.getApi?.(),
    today: () => calendarRef.current?.getApi?.()?.today(),
    prev: () => calendarRef.current?.getApi?.()?.prev(),
    next: () => calendarRef.current?.getApi?.()?.next(),
    changeView: (view) => calendarRef.current?.getApi?.()?.changeView(view),
  }));

  const fcEvents = useMemo(
    () => (events || []).map((ev) => agendaEventToFullCalendar(ev, colors, statusColors)),
    [events, colors, statusColors]
  );

  const handleDatesSet = useCallback(
    (arg) => {
      onDatesChange?.({
        start: arg.start,
        end: arg.end,
        viewType: arg.view.type,
      });
    },
    [onDatesChange]
  );

  const handleEventClick = useCallback(
    (info) => {
      const raw = events.find((e) => String(e.id) === String(info.event.id));
      onEventClick?.(
        raw || {
          id: info.event.id,
          title: info.event.title,
          start: info.event.start,
          end: info.event.end,
          allDay: info.event.allDay,
          source: info.event.extendedProps?.source,
          status: info.event.extendedProps?.status,
          calendarId: info.event.extendedProps?.calendarId,
          calendarName: info.event.extendedProps?.calendarName,
          calendarColor: info.event.extendedProps?.calendarColor,
          statusEditable: info.event.extendedProps?.statusEditable,
          editable: info.event.extendedProps?.editable,
          meta: info.event.extendedProps?.meta,
        }
      );
    },
    [events, onEventClick]
  );

  const handleSelect = useCallback(
    (info) => {
      onDateClick?.({
        start: info.start,
        end: info.end,
        allDay: info.allDay,
      });
      info.view.calendar.unselect();
    },
    [onDateClick]
  );

  const handleEventDrop = useCallback(
    async (info) => {
      const eventId = parseManualEventId(info.event.id);
      if (!eventId || !onEventDrop) {
        info.revert();
        return;
      }
      try {
        await onEventDrop(eventId, {
          start: dayjs(info.event.start).format("YYYY-MM-DD HH:mm:ss"),
          end: info.event.end ? dayjs(info.event.end).format("YYYY-MM-DD HH:mm:ss") : null,
          allDay: info.event.allDay,
        });
      } catch {
        info.revert();
      }
    },
    [onEventDrop]
  );

  const handleEventResize = useCallback(
    async (info) => {
      const eventId = parseManualEventId(info.event.id);
      if (!eventId || !onEventResize) {
        info.revert();
        return;
      }
      try {
        await onEventResize(eventId, {
          start: dayjs(info.event.start).format("YYYY-MM-DD HH:mm:ss"),
          end: info.event.end ? dayjs(info.event.end).format("YYYY-MM-DD HH:mm:ss") : null,
          allDay: info.event.allDay,
        });
      } catch {
        info.revert();
      }
    },
    [onEventResize]
  );

  const handleEventDidMount = useCallback((info) => {
    const status = info.event.extendedProps?.status;
    const statusLabel = status ? t(`statuses.${status}`) : null;
    const activeVideo = info.event.extendedProps?.activeVideo;
    const parts = [statusLabel, info.timeText, info.event.title].filter(Boolean);
    if (activeVideo) {
      parts.unshift(
        activeVideo.isPrivate
          ? t("activeVideo.tooltipPrivate", { ns: "team", defaultValue: "Chamada privada" })
          : t("activeVideo.tooltipJoin", {
              ns: "team",
              defaultValue: "Chamada em andamento — entrar",
            })
      );
    }
    if (parts.length) info.el.setAttribute("title", parts.join(" · "));

    if (activeVideo && info.el && !info.el.querySelector(".agenda-active-video-dot")) {
      const main = info.el.querySelector(".fc-event-main") || info.el;
      const dot = document.createElement("span");
      dot.className = "agenda-active-video-dot";
      if (activeVideo.isPrivate) dot.classList.add("is-private");
      main.appendChild(dot);
    }
  }, [t]);

  const isMonthView = viewKey === "month" || initialView === "dayGridMonth";

  return (
    <Box
      sx={{
        height,
        minHeight: 0,
        "& .fc": {
          "--fc-border-color": theme.palette.divider,
          "--fc-page-bg-color": theme.palette.background.paper,
          "--fc-neutral-bg-color": theme.palette.action.hover,
          "--fc-today-bg-color": alpha(theme.palette.primary.main, 0.06),
          "--fc-now-indicator-color": theme.palette.error.main,
          fontSize: compact ? "0.8rem" : "0.85rem",
        },
        "& .fc-event": {
          cursor: "pointer",
          borderRadius: "6px",
          border: "none",
          boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.12)}`,
        },
        "& .fc-event .fc-event-main": { padding: compact ? "2px 4px" : "3px 5px" },
        "& .fc-event .fc-event-time": {
          fontWeight: 600,
          fontSize: compact ? "0.68rem" : "0.72rem",
          opacity: 0.92,
        },
        "& .fc-event .fc-event-title": {
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        "& .agenda-active-video-dot": {
          position: "absolute",
          top: 4,
          right: 4,
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: theme.palette.error.main,
          boxShadow: `0 0 0 1px ${alpha(theme.palette.common.white, 0.7)}`,
          pointerEvents: "none",
        },
        "& .agenda-active-video-dot.is-private": {
          outline: `1px solid ${alpha(theme.palette.common.black, 0.25)}`,
        },
        "& .fc-event.agenda-active-video .fc-event-main": {
          position: "relative",
        },
        "& .fc-event.agenda-status-completed": {
          opacity: 0.72,
          filter: "grayscale(0.2)",
        },
        "& .fc-event.agenda-status-completed .fc-event-title": {
          textDecoration: "line-through",
        },
        "& .fc-event.agenda-status-canceled": {
          opacity: 0.95,
          filter: "none",
          boxShadow: `inset 3px 0 0 ${theme.palette.error.main}`,
        },
        "& .fc-event.agenda-status-canceled .fc-event-title": {
          textDecoration: "line-through",
        },
        "& .fc-timegrid-now-indicator-line": { borderColor: theme.palette.error.main },
        "& .fc-timegrid-now-indicator-arrow": { borderColor: theme.palette.error.main },
        "& .fc-timegrid-slot-label": {
          color: theme.palette.text.secondary,
          fontSize: compact ? "0.7rem" : "0.75rem",
        },
        "& .fc-scrollgrid": { border: "none" },
        "& .fc-scroller": {
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.25)} transparent`,
        },
        "& .fc-scroller::-webkit-scrollbar": { width: 6 },
        "& .fc-scroller::-webkit-scrollbar-thumb": {
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.text.primary, 0.2),
        },
        ...(!compact
          ? {
              "& .fc-col-header-cell-cushion": {
                textTransform: "capitalize",
                fontWeight: 600,
                fontSize: "0.8rem",
                color: theme.palette.text.secondary,
                py: 0.75,
              },
              "& .fc-daygrid-day-top": { flexDirection: "row", justifyContent: "flex-end", p: 0.5 },
              "& .fc-daygrid-day-number": {
                fontSize: "0.82rem",
                fontWeight: 500,
                color: theme.palette.text.primary,
                p: 0.5,
                lineHeight: 1.2,
              },
              "& .fc-day-other .fc-daygrid-day-number": { color: theme.palette.text.disabled },
              "& .fc-day-today .fc-daygrid-day-number": {
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: "50%",
                width: 26,
                height: 26,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              },
              "& .fc-daygrid-event": { marginTop: "2px", borderRadius: "4px" },
              "& .fc-daygrid-event .fc-event-time": { fontWeight: 700, fontSize: "0.7rem" },
              "& .fc-daygrid-event .fc-event-title": { fontWeight: 500 },
              "& .fc-daygrid-more-link": {
                fontWeight: 600,
                fontSize: "0.72rem",
                color: theme.palette.primary.main,
              },
              "& .fc-list-day-cushion": {
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                fontWeight: 600,
              },
              "& .fc-list-event-time": { fontWeight: 600, color: theme.palette.text.secondary },
            }
          : {}),
        ...(compact
          ? {
              "& .fc-col-header": { display: "none" },
              "& .fc-timegrid-axis": { width: "34px !important" },
              "& .fc-timegrid-allday .fc-scrollgrid-sync-table": { minHeight: 26 },
              "& .fc-timegrid-allday .fc-timegrid-axis-cushion": {
                fontSize: "0.65rem",
                lineHeight: 1.2,
                padding: "2px 4px",
              },
              "& .fc-timegrid-slot": { height: "2.25em" },
            }
          : {}),
      }}
    >
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
        initialView={initialView}
        headerToolbar={headerToolbar ?? false}
        locale={fcLocale}
        events={fcEvents}
        height={height}
        nowIndicator={nowIndicator}
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        allDaySlot
        selectable
        selectMirror
        editable={editableManualOnly}
        eventStartEditable={(ev) => Boolean(ev.event.extendedProps?.editable)}
        eventDurationEditable={(ev) => Boolean(ev.event.extendedProps?.editable)}
        datesSet={handleDatesSet}
        eventClick={handleEventClick}
        select={handleSelect}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventDidMount={handleEventDidMount}
        dayMaxEvents={isMonthView ? 4 : false}
        fixedWeekCount={false}
        slotDuration="00:30:00"
        defaultTimedEventDuration="01:00:00"
        forceEventDuration
        slotLabelFormat={{ hour: "numeric", minute: "2-digit", hour12: false }}
        eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
        displayEventEnd
        scrollTime={dayjs().format("HH:mm:ss")}
        scrollTimeReset={false}
      />
    </Box>
  );
});

export default AgendaCalendarCore;
